import { QUESTIONS } from './questions.config';
import { TAnswers } from './types';

// Boundaries are SCHUFA's published score classes; the sub-scores come from our observed
// approval rate by Branchenscore grade (776+ ≈ A–C, 642+ ≈ D–H, 100+ ≈ I–M), so A–C sits
// ~1.5x above D–H and ~10x above I–M. Deliberately coarse — finer bands would leak our
// credit cuts and would need redoing when SCHUFA converges our grade onto this scale.
const SCHUFA_BANDS = [
  { min: 776, subScore: 0.85 },
  { min: 642, subScore: 0.57 },
  { min: 100, subScore: 0.08 },
] as const;
// The scale starts at 100, so anything below it is not a score we can assess.
const SCHUFA_SUB_SCORE_FLOOR = 0;

const CREFO_BEST_INDEX = 100;
const CREFO_WORST_INDEX = 600;
const CREFO_SUB_SCORE_FLOOR = 0.1;
const CREFO_SUB_SCORE_RANGE = 0.9;

const REGISTERED_2M_YES_SUB_SCORE = 1;
const REGISTERED_2M_NO_SUB_SCORE = 0.08;
const REVENUE_22K_YES_SUB_SCORE = 1;
const REVENUE_22K_NO_SUB_SCORE = 0.12;

const CONFIDENCE_FLOOR = 0.55;
const CONFIDENCE_RANGE = 0.45;
const TOTAL_WEIGHT = 1.0;

const schufaSubScore = (score: number): number => {
  const band = SCHUFA_BANDS.find(({ min }) => score >= min);
  return band ? band.subScore : SCHUFA_SUB_SCORE_FLOOR;
};

const crefoSubScore = (index: number): number => {
  const normalised =
    (CREFO_WORST_INDEX - index) / (CREFO_WORST_INDEX - CREFO_BEST_INDEX);
  return Math.max(
    CREFO_SUB_SCORE_FLOOR,
    Math.min(1, CREFO_SUB_SCORE_FLOOR + normalised * CREFO_SUB_SCORE_RANGE),
  );
};

const DIMENSION_WEIGHTS = [
  {
    weight: 0.24,
    subScore: (answers: TAnswers) =>
      answers.registered2m
        ? answers.registered2m === 'yes'
          ? REGISTERED_2M_YES_SUB_SCORE
          : REGISTERED_2M_NO_SUB_SCORE
        : null,
  },
  {
    weight: 0.24,
    subScore: (answers: TAnswers) =>
      answers.revenueOver22k
        ? answers.revenueOver22k === 'yes'
          ? REVENUE_22K_YES_SUB_SCORE
          : REVENUE_22K_NO_SUB_SCORE
        : null,
  },
  {
    weight: 0.3,
    subScore: (answers: TAnswers) =>
      typeof answers.schufaScore === 'number'
        ? schufaSubScore(answers.schufaScore)
        : null,
  },
  {
    weight: 0.22,
    subScore: (answers: TAnswers) =>
      typeof answers.crefoIndex === 'number'
        ? crefoSubScore(answers.crefoIndex)
        : null,
  },
];

export const answeredCount = (answers: TAnswers): number =>
  QUESTIONS.reduce((count, question) => {
    const value = answers[question.id];
    const answered =
      question.kind === 'number' ? typeof value === 'number' : Boolean(value);
    return answered ? count + 1 : count;
  }, 0);

export const likelihood = (answers: TAnswers): number => {
  let accumulated = 0;
  let weightSum = 0;
  for (const dimension of DIMENSION_WEIGHTS) {
    const subScore = dimension.subScore(answers);
    if (subScore === null) continue;
    accumulated += dimension.weight * subScore;
    weightSum += dimension.weight;
  }
  if (weightSum === 0) return 0;
  const raw = accumulated / weightSum;
  const coverage = weightSum / TOTAL_WEIGHT;
  // Confidence ceiling scales with coverage: ~55% for one question, 100% for all four.
  const ceiling = CONFIDENCE_FLOOR + CONFIDENCE_RANGE * coverage;
  return Math.round(raw * ceiling * 100);
};
